import {
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { json, redirect } from "@remix-run/node";
import type { LoaderFunction } from "@remix-run/node";
import { useSearchParams } from "@remix-run/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthPageLayout } from "~/components/AuthPageLayout";
import { InAppBrowserNotice } from "~/components/InAppBrowserNotice";
import {
  TelegramDeepLinkLoginButton,
  type TelegramLoginProgress,
} from "~/components/TelegramDeepLinkLoginButton";
import { getAuthData } from "~/ssr/auth.server";
import { LOGIN_PROVIDERS, type LoginProviderConfig } from "~/utils/authProviders";
import {
  addAuthBreadcrumb,
  captureAuthEvent,
  detectInAppBrowser,
  type InAppPlatform,
} from "~/utils/authTelemetry";
import { safeRedirectPath } from "~/utils/safeRedirectPath";
import { supabase } from "~/utils/supabase.client";

const LAST_OAUTH_ATTEMPT_KEY = "auth.lastOAuthAttempt";
const SAFETY_BANNER_WINDOW_MS = 60_000;
const WEBVIEW_ERROR_DESC_PATTERN =
  /disallowed_useragent|user.?agent|browser|webview/i;

/**
 * Translate a Supabase OAuth error into a user-facing i18n key.
 *
 * Supabase delivers provider errors via the URL hash fragment
 * (e.g. "#error=access_denied&error_code=provider_email_needs_verification"),
 * which never reaches the server nor the Network tab, so without explicit
 * UI feedback the page just silently reloads.
 */
function resolveOAuthErrorKey(
  errorCode: string | null,
  errorDescription: string | null,
): string | null {
  if (errorCode === "provider_email_needs_verification") {
    return "auth.login.errors.emailNeedsVerification";
  }
  if (errorDescription || errorCode) {
    return "auth.login.errors.generic";
  }
  return null;
}

export const loader: LoaderFunction = async ({ request }) => {
  const responseHeaders = new Headers();
  const authData = await getAuthData(request, responseHeaders);
  if (authData) {
    if (!authData.tosAgreed) {
      return redirect("/signup", { headers: responseHeaders });
    }
    const url = new URL(request.url);
    const teamId = url.searchParams.get("teamId");
    const redirectTo = safeRedirectPath(url.searchParams.get("redirectTo"));
    const dest =
      redirectTo ?? (teamId ? `/select-team?teamId=${teamId}` : "/select-team");
    return redirect(dest, { headers: responseHeaders });
  }
  return json(null, { headers: responseHeaders });
};

type ErrorMessageSpec =
  | {
      kind: "key";
      key: string;
      description?: string;
      severity?: "error" | "warning";
    }
  | null;

export default function LoginPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const teamId = searchParams.get("teamId");
  const [errorSpec, setErrorSpec] = useState<ErrorMessageSpec>(null);
  const [showAccountLinkedInfo, setShowAccountLinkedInfo] = useState(false);
  const [inAppNoticeOpen, setInAppNoticeOpen] = useState(false);
  const [inAppPlatform, setInAppPlatform] = useState<InAppPlatform>("desktop");
  const [showTroubleBanner, setShowTroubleBanner] = useState(false);
  const [telegramProgress, setTelegramProgress] =
    useState<TelegramLoginProgress>("idle");

  const errorMessage = errorSpec
    ? t(errorSpec.key, { description: errorSpec.description ?? "" })
    : null;
  const errorSeverity = errorSpec?.severity ?? "error";
  const errorAlertStyle =
    errorSeverity === "warning"
      ? {
          borderColor: theme.palette.custom.yellow[300],
          backgroundColor: theme.palette.custom.yellow[100],
          textColor: theme.palette.custom.gray[800],
        }
      : {
          borderColor: theme.palette.custom.destructive,
          backgroundColor: theme.palette.custom.destructiveSecondaryHover,
          textColor: theme.palette.custom.destructive,
        };
  const infoMessage = showAccountLinkedInfo
    ? t("auth.login.info.accountLinked")
    : null;
  const progressMessageMap: Record<TelegramLoginProgress, string | null> = {
    idle: null,
    polling: t("auth.login.telegramProgress.polling"),
    consuming: t("auth.login.telegramProgress.consuming"),
  };
  const progressMessage = progressMessageMap[telegramProgress];

  useEffect(() => {
    try {
      localStorage.removeItem("selectedTeamId");
      localStorage.removeItem("selectedProjectId");
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  useEffect(() => {
    // Supabase puts OAuth errors in the URL hash, our own callback uses query.
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    const hashParams = new URLSearchParams(hash);
    const hashError = hashParams.get("error");
    const errorCode = hashParams.get("error_code");
    const errorDescription = hashParams.get("error_description");
    const queryError = searchParams.get("error");
    const queryInfo = searchParams.get("info");

    const detection = detectInAppBrowser();
    setInAppPlatform(detection.platform);

    const hasAnyError = Boolean(hashError || errorCode || queryError);
    const descriptionLooksLikeWebview =
      errorDescription != null &&
      WEBVIEW_ERROR_DESC_PATTERN.test(errorDescription);
    const isAccessDenied =
      hashError === "access_denied" || queryError === "access_denied";
    const isLikelyWebviewBlock =
      detection.isInApp && (descriptionLooksLikeWebview || isAccessDenied);
    // Telegram's webview blocks Google/Slack/Discord OAuth, so prompt the
    // external-browser guide before the user even tries.
    const isTelegramInApp =
      detection.isInApp && detection.appName === "telegram";

    if (isTelegramInApp) {
      setInAppNoticeOpen(true);
      captureAuthEvent("auth.in_app_browser_telegram_detected", "info", {
        inAppName: detection.appName,
      });
    } else if (isLikelyWebviewBlock) {
      setInAppNoticeOpen(true);
      captureAuthEvent("auth.in_app_browser_block_detected", "warning", {
        hashError,
        errorCode,
        errorDescription,
        queryError,
        inAppName: detection.appName,
      });
    } else {
      const key = resolveOAuthErrorKey(errorCode, errorDescription);
      if (key) {
        setErrorSpec({
          kind: "key",
          key,
          description: errorDescription ?? "",
        });
      } else if (queryError === "provider_missing_email") {
        setErrorSpec({ kind: "key", key: "auth.login.errors.providerMissingEmail" });
      } else if (queryError === "auth_callback_failed") {
        setErrorSpec({ kind: "key", key: "auth.login.errors.callbackFailed" });
      } else if (
        queryError === "telegram_invalid" ||
        queryError === "telegram_failed" ||
        queryError === "telegram_otp"
      ) {
        setErrorSpec({ kind: "key", key: "auth.login.errors.telegramFailed" });
      }
    }

    if (queryInfo === "account_linked") {
      setShowAccountLinkedInfo(true);
    }

    if (hasAnyError && !isLikelyWebviewBlock) {
      captureAuthEvent("auth.oauth_error_shown", "warning", {
        hashError,
        errorCode,
        errorDescription,
        queryError,
      });
    }

    // Safety net: Google often shows its own "Couldn't sign you in" page and
    // never redirects back, so we never see an error param. If the user lands
    // back on /login soon after clicking an OAuth button from a webview, show
    // a non-blocking banner pointing to the help modal.
    if (detection.isInApp && !isLikelyWebviewBlock) {
      try {
        const raw = sessionStorage.getItem(LAST_OAUTH_ATTEMPT_KEY);
        if (raw) {
          const elapsed = Date.now() - Number(raw);
          if (elapsed >= 0 && elapsed < SAFETY_BANNER_WINDOW_MS) {
            setShowTroubleBanner(true);
          }
        }
      } catch {
        // sessionStorage may be blocked.
      }
    }

    // Strip the hash so the banner doesn't survive a retry.
    if (hash) {
      const url = new URL(window.location.href);
      url.hash = "";
      window.history.replaceState(null, "", url.toString());
    }
    // Reading URL params + sessionStorage is a one-shot read that must not
    // re-run on language change — that would reopen the modal after the user
    // dismissed it. `t` is applied lazily at render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSignIn = async (config: LoginProviderConfig) => {
    addAuthBreadcrumb("oauth.signin_started", { provider: config.id });
    try {
      sessionStorage.setItem(LAST_OAUTH_ATTEMPT_KEY, Date.now().toString());
    } catch {
      // sessionStorage may be blocked; safety banner just won't fire.
    }
    const callbackUrl = new URL(`${window.location.origin}/api/auth/callback`);

    // Only set "next" when there's a specific destination — callback defaults to /select-team
    const nextUrl = teamId ? `/select-team?teamId=${teamId}` : redirectTo;
    if (nextUrl) {
      callbackUrl.searchParams.set("next", nextUrl);
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: config.id,
      options: {
        redirectTo: callbackUrl.toString(),
        ...(config.scopes ? { scopes: config.scopes } : {}),
        ...(config.queryParams ? { queryParams: config.queryParams } : {}),
      },
    });
    if (error) {
      captureAuthEvent("auth.oauth_signin_failed", "error", {
        provider: config.id,
        message: error.message,
      });
    }
  };

  return (
    <AuthPageLayout showFooterLinks>
      {showTroubleBanner && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Box
            role="status"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.custom.yellow[300]}`,
              backgroundColor: theme.palette.custom.yellow[100],
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: 1.5,
                color: theme.palette.custom.gray[800],
              }}
            >
              {t("auth.login.inAppBrowser.troubleSigningIn")}
            </Typography>
            <Link
              component="button"
              type="button"
              onClick={() => setInAppNoticeOpen(true)}
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.custom.primary,
                textDecoration: "none",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {t("auth.login.inAppBrowser.seeHelp")}
            </Link>
          </Box>
        </Box>
      )}
      <Box sx={{ px: 3, py: 2 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 24, sm: 32 },
            lineHeight: 1.33,
            letterSpacing: "-0.32px",
            color: theme.palette.custom.gray[900],
            mb: 1.5,
          }}
        >
          {t("auth.login.title")}
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            lineHeight: 1.6,
            color: theme.palette.custom.gray[700],
          }}
        >
          {t("auth.login.description")}
        </Typography>
      </Box>

      {errorMessage && (
        <Box sx={{ px: 3 }}>
          <Box
            role="alert"
            sx={{
              px: 2,
              py: 1.5,
              border: 1,
              borderColor: errorAlertStyle.borderColor,
              backgroundColor: errorAlertStyle.backgroundColor,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                color: errorAlertStyle.textColor,
              }}
            >
              {errorMessage}
            </Typography>
          </Box>
        </Box>
      )}

      {progressMessage && !errorMessage && (
        <Box sx={{ px: 3 }}>
          <Box
            role="status"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.5,
              border: 1,
              borderColor: theme.palette.custom.primary,
              backgroundColor: theme.palette.custom.primarySecondaryHover,
              borderRadius: "8px",
            }}
          >
            <CircularProgress
              size={16}
              thickness={5}
              sx={{ color: theme.palette.custom.primary }}
            />
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                color: theme.palette.custom.primary,
              }}
            >
              {progressMessage}
            </Typography>
          </Box>
        </Box>
      )}

      {infoMessage && !errorMessage && !progressMessage && (
        <Box sx={{ px: 3 }}>
          <Box
            role="status"
            sx={{
              px: 2,
              py: 1.5,
              border: 1,
              borderColor: theme.palette.custom.primary,
              backgroundColor: theme.palette.custom.primarySecondaryHover,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                color: theme.palette.custom.primary,
              }}
            >
              {infoMessage}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ px: 3, pt: 2 }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.5,
            color: theme.palette.custom.gray[800],
          }}
        >
          {t("auth.login.signInWith")}
        </Typography>
      </Box>
      <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {LOGIN_PROVIDERS.map((provider) => (
          <Button
            key={provider.id}
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={
              <Box
                component="img"
                src={provider.icon}
                alt=""
                sx={{ width: 24, height: 24 }}
              />
            }
            onClick={() => handleSignIn(provider)}
            sx={{
              minHeight: 52,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
              color: theme.palette.custom.primary,
              border: `1px solid ${theme.palette.custom.primary}`,
              "&:hover": {
                backgroundColor: theme.palette.custom.primarySecondaryHover,
                borderColor: theme.palette.custom.primaryHover,
              },
              "&:active": {
                backgroundColor: theme.palette.custom.primarySecondaryPressed,
                borderColor: theme.palette.custom.primaryPressed,
              },
            }}
          >
            {t(provider.labelKey)}
          </Button>
        ))}
        <TelegramDeepLinkLoginButton
          next={teamId ? `/select-team?teamId=${teamId}` : redirectTo}
          label={t("auth.login.telegramButton")}
          onError={(key, severity) =>
            setErrorSpec({ kind: "key", key, severity })
          }
          onProgress={setTelegramProgress}
        />
      </Box>
      <InAppBrowserNotice
        open={inAppNoticeOpen}
        onClose={() => setInAppNoticeOpen(false)}
        platform={inAppPlatform}
      />
    </AuthPageLayout>
  );
}
